import { classNames } from '@/utils';

import Button, { ButtonSize, ButtonStyle } from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import Modal, { ModalSize } from '@/components/base/Modal';

export type ModalAction = {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    hidden?: boolean;
    size?: keyof typeof ButtonSize;
    variant?: keyof typeof ButtonStyle;
    className?: string;
    icon?: string;
    testId?: string;
};

type QuickActionsModalProps = {
    isOpen: boolean;
    onDismiss?: () => void;
    title: string;
    description?: string;
    actions: ModalAction[];
    withCloseButton?: boolean;
    size?: keyof typeof ModalSize;
    containerClassName?: string;
    titleClassName?: string;
    descriptionClassName?: string;
    footerClassName?: string;
    children?: React.ReactNode;
    testId?: string;
};

const QuickActionsModal = ({
    isOpen,
    onDismiss,
    title,
    description,
    actions,
    withCloseButton = true,
    size = 'DEFAULT',
    containerClassName,
    titleClassName,
    descriptionClassName,
    footerClassName,
    children,
    testId
}: QuickActionsModalProps) => {
    const hasMultipleActions = actions.length > 1;

    return (
        <Modal size={size} open={isOpen} onDismiss={onDismiss} withCloseButton={withCloseButton}>
            <div
                className={classNames('flex flex-col gap-8 p-10', containerClassName)}
                data-testid={testId}>
                <Modal.Title className={classNames('text-left', titleClassName)}>
                    {title}
                </Modal.Title>

                {(description || children) && (
                    <Modal.Body className="p-0">
                        {description && (
                            <p
                                className={classNames(
                                    'text-md text-left font-GMedium text-icoGray-500',
                                    descriptionClassName
                                )}>
                                {description}
                            </p>
                        )}
                        {children}
                    </Modal.Body>
                )}

                <Modal.Footer className={classNames('flex w-full gap-3 p-0', footerClassName)}>
                    {actions.map((action, index) => {
                        const isSecondaryAction = hasMultipleActions && index > 0;
                        const buttonWidth = hasMultipleActions ? 'w-[50%]' : 'w-full';
                        if (action.hidden) {
                            return null;
                        }
                        return (
                            <Button
                                key={action.label}
                                onClick={action.onClick}
                                variant={
                                    action.variant || (isSecondaryAction ? 'secondary' : 'primary')
                                }
                                size={action.size}
                                disabled={action.disabled}
                                data-testid={action.testId}
                                className={classNames(
                                    'font-GSemiBold text-sm',
                                    buttonWidth,
                                    action.icon && 'gap-3',
                                    action.className
                                )}>
                                {action.icon && <Icon icon={action.icon} />}
                                {action.label}
                            </Button>
                        );
                    })}
                </Modal.Footer>
            </div>
        </Modal>
    );
};

export default QuickActionsModal;
